/**
 * Rhythmz — Scene: LSD Cuttlefish
 * ──────────────────────────────────
 * A bioluminescent deep-sea entity. Self-illuminated (no external lights needed)
 * with tentacles undulating in 3D sine waves. Full spectrum color cycling —
 * purple → cyan → hot pink → green → gold.
 *
 * Audio: Bass → mantle pulse + tentacle wave speed. Mid → color cycling speed.
 *        Beat → tentacle whip + eye flash.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, vec3, vec4, float, sin, cos, fract, dot, floor, abs,
  add, sub, mul, mix, pow, normalize,
  positionLocal, normalLocal, positionWorld, cameraPosition,
} from 'three/tsl';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface CuttlefishUniforms {
  uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N;
  uCol0: N; uCol1: N; uCol2: N;
}

// 3D noise for mantle surface
const hash3 = Fn(([p]: [N]) => fract(sin(dot(p, vec3(127.1, 311.7, 74.7))).mul(float(43758.5453))));
const vnoise3 = Fn(([p]: [N]) => {
  const i = floor(p); const f: N = fract(p);
  const ux = mul(f.x, f.x, sub(float(3.0), mul(float(2.0), f.x)));
  const uy = mul(f.y, f.y, sub(float(3.0), mul(float(2.0), f.y)));
  const uz = mul(f.z, f.z, sub(float(3.0), mul(float(2.0), f.z)));
  const n000 = hash3(i), n100 = hash3(add(i, vec3(1,0,0)));
  const n010 = hash3(add(i, vec3(0,1,0))), n110 = hash3(add(i, vec3(1,1,0)));
  const n001 = hash3(add(i, vec3(0,0,1))), n101 = hash3(add(i, vec3(1,0,1)));
  const n011 = hash3(add(i, vec3(0,1,1))), n111 = hash3(add(i, vec3(1,1,1)));
  return mix(mix(mix(n000,n100,ux), mix(n010,n110,ux), uy), mix(mix(n001,n101,ux), mix(n011,n111,ux), uy), uz);
});
const fbm3 = Fn(([p]: [N]) => {
  let v: N = float(0.0); let a: N = float(0.5); let freq: N = float(1.0);
  v = add(v, mul(a, vnoise3(mul(p, freq)))); freq = mul(freq, float(2.0)); a = mul(a, float(0.5));
  v = add(v, mul(a, vnoise3(mul(p, freq)))); freq = mul(freq, float(2.0)); a = mul(a, float(0.5));
  v = add(v, mul(a, vnoise3(mul(p, freq)))); freq = mul(freq, float(2.0)); a = mul(a, float(0.5));
  v = add(v, mul(a, vnoise3(mul(p, freq))));
  return v;
});

// Full-spectrum color cycle — Inigo Quilez cosine palette
const spectrum = Fn(([t]: [N]) => {
  const a = vec3(0.5, 0.5, 0.5);
  const b = vec3(0.5, 0.5, 0.5);
  const c = vec3(1.0, 1.0, 1.0);
  const d = vec3(0.0, 0.33, 0.67);
  return add(a, mul(b, cos(add(mul(float(6.2831), add(t, d)), c))));
});

export function createCuttlefish(u: CuttlefishUniforms): SceneHandle {
  const group = new THREE.Group();

  // ── Mantle (body): high-detail icosahedron with extreme displacement ──
  const mantleGeo = new THREE.IcosahedronGeometry(0.8, 4);
  const mantleMat = new THREE.MeshStandardNodeMaterial({
    metalness: 0.0,
    roughness: 0.4,
  });

  mantleMat.positionNode = Fn(() => {
    const pos = positionLocal;
    const norm = normalLocal;
    const n = fbm3(add(mul(pos, float(1.5)), mul(u.uTime, float(0.3))));
    const bassPulse = mul(u.uBass, float(0.5));
    const beatPulse = mul(u.uBeat, float(0.3));
    const disp = mul(add(sub(n, float(0.5)), bassPulse, beatPulse), float(0.35));
    return add(pos, mul(norm, disp));
  })();

  mantleMat.normalNode = Fn(() => {
    const pos = positionLocal;
    const n = fbm3(add(mul(pos, float(1.5)), mul(u.uTime, float(0.3))));
    const eps = float(0.04);
    const nX = fbm3(add(mul(pos, float(1.5)), add(vec3(eps, 0, 0), mul(u.uTime, float(0.3)))));
    const nY = fbm3(add(mul(pos, float(1.5)), add(vec3(0, eps, 0), mul(u.uTime, float(0.3)))));
    return normalize(vec3(sub(nX, n), sub(nY, n), float(0.5)));
  })();

  mantleMat.emissiveNode = Fn(() => {
    const n = fbm3(add(mul(positionLocal, float(1.5)), mul(u.uTime, float(0.3))));
    const colorT = fract(add(mul(u.uTime, float(0.15)), mul(n, float(0.2))));
    const col = spectrum(colorT);
    const viewDir = normalize(cameraPosition.sub(positionWorld));
    const fres = sub(float(1.0), abs(dot(normalLocal, viewDir)));
    const rim = pow(fres, float(2.0));
    const glow = add(mul(col, float(0.6)), mul(col, mul(rim, float(1.2))));
    return vec4(glow.x, glow.y, glow.z, float(1.0));
  })();

  const mantle = new THREE.Mesh(mantleGeo, mantleMat);
  mantle.position.set(0, 0.5, 0);
  group.add(mantle);

  // ── Tentacles: 8 tubes, each undulating ──
  const tentacleCount = 8;
  const tentacleGroup = new THREE.Group();
  group.add(tentacleGroup);

  const tentacles: { mesh: THREE.Mesh; geo: THREE.TubeGeometry; phase: number; side: number }[] = [];

  for (let i = 0; i < tentacleCount; i++) {
    const angle = (i / tentacleCount) * Math.PI * 2;
    const side = Math.cos(angle);
    // Build a tube along a curve — we'll update the path on CPU each frame
    const points: THREE.Vector3[] = [];
    for (let j = 0; j <= 12; j++) {
      const t = j / 12;
      points.push(new THREE.Vector3(
        Math.cos(angle) * t * 1.2,
        0.5 - t * 1.5,
        Math.sin(angle) * t * 1.2,
      ));
    }
    const curve = new THREE.CatmullRomCurve3(points);
    const geo = new THREE.TubeGeometry(curve, 12, 0.06 + (1 - 0) * 0.04, 6, false);
    const mat = new THREE.MeshStandardNodeMaterial({
      metalness: 0.0,
      roughness: 0.3,
    });

    mat.emissiveNode = Fn(() => {
      const tentacleIdx = float(i);
      const colorT = fract(add(add(mul(u.uTime, float(0.15)), mul(tentacleIdx, float(0.1))), mul(positionLocal.y, float(0.3))));
      const col = spectrum(colorT);
      const whip = mul(u.uBeat, float(1.5));
      const bright = add(mul(col, float(0.5)), mul(col, whip));
      return vec4(bright.x, bright.y, bright.z, float(1.0));
    })();

    const mesh = new THREE.Mesh(geo, mat);
    tentacleGroup.add(mesh);
    tentacles.push({ mesh, geo, phase: i * 0.8, side });
  }

  // ── Eyes: two emissive spheres on stalks ──
  const eyeGeo = new THREE.SphereGeometry(0.12, 12, 12);
  const eyeMat = new THREE.MeshStandardNodeMaterial({ metalness: 0.0, roughness: 0.0 });
  eyeMat.emissiveNode = Fn(() => {
    const colorT = fract(mul(u.uTime, float(0.3)));
    const col = spectrum(colorT);
    const flash = add(float(0.8), mul(u.uBeat, float(1.5)));
    return vec4(mul(col.x, flash), mul(col.y, flash), mul(col.z, flash), float(1.0));
  })();

  const eyeL = new THREE.Mesh(eyeGeo, eyeMat);
  eyeL.position.set(-0.3, 1.1, 0.3);
  group.add(eyeL);
  const eyeR = new THREE.Mesh(eyeGeo, eyeMat);
  eyeR.position.set(0.3, 1.1, 0.3);
  group.add(eyeR);

  // ── Animation ──
  let t = 0;

  const update = (dt: number): void => {
    t += dt;

    // Group floats slowly
    group.position.y = Math.sin(t * 0.3) * 0.2;
    group.rotation.y = Math.sin(t * 0.2) * 0.3;
    group.rotation.z = Math.sin(t * 0.15) * 0.1;

    // Mantle pulse on bass
    const bassPulse = 1 + (u.uBass.value ?? 0) * 0.15;
    mantle.scale.setScalar(bassPulse);

    // Tentacles undulate — update each tube geometry's curve
    const waveSpeed = 1.5 + (u.uMid.value ?? 0) * 2.0;
    const beatWhip = (u.uBeat.value ?? 0) * 0.4;
    tentacles.forEach((tent, i) => {
      const angle = (i / tentacleCount) * Math.PI * 2;
      const points: THREE.Vector3[] = [];
      for (let j = 0; j <= 12; j++) {
        const segT = j / 12;
        const wave = Math.sin(segT * 4 + t * waveSpeed + tent.phase) * 0.2 * segT;
        const droop = -segT * 1.5;
        const whip = beatWhip * Math.sin(segT * 3 + tent.phase) * segT;
        points.push(new THREE.Vector3(
          Math.cos(angle) * segT * 1.2 + wave * Math.cos(angle + 1.57),
          0.5 + droop + whip,
          Math.sin(angle) * segT * 1.2 + wave * Math.sin(angle + 1.57),
        ));
      }
      // Rebuild the tube with new curve
      tent.geo.dispose();
      const curve = new THREE.CatmullRomCurve3(points);
      tent.geo = new THREE.TubeGeometry(curve, 12, 0.06 * (1 - 0 + 0.3), 6, false);
      tent.mesh.geometry = tent.geo;
    });

    // Eyes track and pulse
    eyeL.position.set(-0.3 + Math.sin(t * 0.5) * 0.05, 1.1 + Math.cos(t * 0.4) * 0.03, 0.3);
    eyeR.position.set(0.3 + Math.cos(t * 0.5) * 0.05, 1.1 + Math.sin(t * 0.4) * 0.03, 0.3);
  };

  const dispose = (): void => {
    mantleGeo.dispose(); mantleMat.dispose();
    tentacles.forEach(t => { t.geo.dispose(); (t.mesh.material as THREE.Material).dispose(); });
    eyeGeo.dispose(); eyeMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'LSD Cuttlefish' };
}